import React, { useMemo, useState } from "react";
import { api } from "../api/client.js";
import { colors } from "../utils/theme.js";
import { inputStyle, primaryBtn, ghostBtn, labelStyle, linkBtn } from "../clients/ui.js";
import { findDuplicates } from "./crmLogic.js";

export const CONTACT_INPUTS = [
  ["name", "Name"], ["organization", "Organization"], ["role", "Role"], ["base", "Base"],
  ["expertise", "Expertise"], ["email", "Email"], ["phone", "Phone"], ["source", "How we met"],
];
export const splitTags = (s) => String(s || "").split(/[;,]/).map((x) => x.trim()).filter(Boolean);

// Add a contact to the shared book. While typing, matches already in the book show up: an
// email match blocks saving (the same person) and offers Link instead (client tab) / Open
// (Contacts page); a similar name only warns. A server 409 is handled the same way.
// mode: "client" → onLink(contactId); "book" → onOpen(contactId).
export default function ContactForm({ contacts, mode = "book", onCreated, onLink, onOpen, onCancel }) {
  const [form, setForm] = useState({ name: "", organization: "", role: "", base: "", expertise: "", email: "", phone: "", source: "" });
  const [busy, setBusy] = useState(false);
  const [serverDup, setServerDup] = useState(null);
  const [error, setError] = useState(null);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (k === "email") setServerDup(null); };
  const dups = useMemo(() => findDuplicates(contacts, { name: form.name, email: form.email }), [contacts, form.name, form.email]);
  const emailDupId = dups.emailMatch?.id || serverDup;
  const emailDup = emailDupId ? (contacts || []).find((c) => c.id === emailDupId) || { id: emailDupId, name: "This person" } : null;
  const canSave = form.name.trim() && !emailDup && !busy;

  const act = (id) => (mode === "client" ? onLink(id) : onOpen(id));
  const actLabel = mode === "client" ? "Link instead" : "Open";

  const save = async () => {
    setBusy(true); setError(null);
    const r = await api.contacts.create({ ...form, expertise: splitTags(form.expertise) });
    setBusy(false);
    if (r.ok) return onCreated(r.data);
    if (r.status === 409 && r.data?.existingId) return setServerDup(r.data.existingId);
    setError(r.data?.error || "Couldn’t save the contact.");
  };

  const notice = (tone, children) => (
    <div style={{ fontSize: 12.5, padding: "8px 10px", borderRadius: 6, marginTop: 6, background: tone === "danger" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)", color: tone === "danger" ? "#fca5a5" : "#fcd34d" }}>{children}</div>
  );
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {CONTACT_INPUTS.map(([k, label]) => (
          <label key={k} style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: k === "name" || k === "expertise" ? "1 / -1" : undefined }}>
            <span style={labelStyle}>{label}</span>
            <input aria-label={label} style={inputStyle} value={form[k]} onChange={(e) => set(k, e.target.value)}
              placeholder={k === "expertise" ? "Comma-separated, e.g. counter-UAS, EW" : ""} />
          </label>
        ))}
      </div>
      {emailDup && notice("danger", <>
        <b>{emailDup.name}</b>{emailDup.organization ? ` (${emailDup.organization})` : ""} is already in the contact book with this email.{" "}
        <button style={linkBtn} onClick={() => act(emailDup.id)}>{actLabel}</button>
      </>)}
      {!emailDup && dups.nameMatches.length > 0 && notice("warn", <>
        Similar name already in the book: {dups.nameMatches.map((c, i) => (
          <span key={c.id}>{i ? ", " : ""}<b>{c.name}</b>{c.organization ? ` (${c.organization})` : ""} <button style={linkBtn} onClick={() => act(c.id)}>{actLabel}</button></span>
        ))}. Save anyway if this is a different person.
      </>)}
      {error && <div style={{ color: colors.danger, fontSize: 12.5, marginTop: 8 }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
        <button onClick={onCancel} style={ghostBtn}>Cancel</button>
        <button onClick={save} disabled={!canSave} style={{ ...primaryBtn, opacity: canSave ? 1 : 0.5 }}>Save contact</button>
      </div>
    </div>
  );
}
